"use client";

import { useState, useEffect } from "react";
import Button from "../ui/Button";
import { DevicePhoneMobileIcon, ShareIcon } from "@heroicons/react/24/outline";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function detectIOS() {
  const hasNavigator = typeof navigator !== "undefined";
  const hasWindow = typeof window !== "undefined";
  if (!hasNavigator) {
    return false;
  }
  const isAppleDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const hasMsStream = hasWindow && "MSStream" in window;
  return isAppleDevice && !hasMsStream;
}

function detectStandalone() {
  if (typeof window === "undefined") {
    return false;
  }
  const mediaStandalone = window.matchMedia(
    "(display-mode: standalone)"
  ).matches;
  const navigatorStandalone =
    "standalone" in window.navigator &&
    Boolean(
      (window.navigator as Navigator & { standalone?: boolean }).standalone
    );

  return mediaStandalone || navigatorStandalone;
}

export default function InstallPrompt() {
  const isIOS = detectIOS();
  const isStandalone = detectStandalone();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if user dismissed recently
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    if (dismissed) {
      const dismissedDate = parseInt(dismissed);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedDate < sevenDays) {
        return;
      }
    }

    // Listen for the beforeinstallprompt event (Chrome, Edge)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      console.log("User accepted the install prompt");
    } else {
      console.log("User dismissed the install prompt");
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Store in localStorage to not show again for 7 days
    localStorage.setItem("pwa-install-dismissed", Date.now().toString());
  };

  // Don't show if already installed or dismissed
  if (isStandalone || !showPrompt) {
    return null;
  }

  // iOS specific prompt
  if (isIOS) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-gray-200 shadow-lg md:hidden">
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <DevicePhoneMobileIcon className="w-8 h-8 text-mygreen" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">
              Instale o App Santa Fé Conecta
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Para instalar este app no seu iPhone/iPad, toque no ícone de
              compartilhar <ShareIcon className="w-4 h-4 inline" /> e depois
              &quot;Adicionar à Tela de Início&quot;{" "}
              <span role="img" aria-label="plus icon">
                ➕
              </span>
            </p>
            <button
              onClick={handleDismiss}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Agora não
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop prompt (uses beforeinstallprompt)
  if (showPrompt && deferredPrompt) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 md:hidden">
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <DevicePhoneMobileIcon className="w-8 h-8 text-mygreen" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">
              Instale o Santa Fé Conecta
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Adicione à sua tela inicial para acesso rápido e receba
              notificações de eventos e novidades.
            </p>
            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={handleInstallClick}>
                Instalar
              </Button>
              <Button variant="outline" size="sm" onClick={handleDismiss}>
                Depois
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
